import React, { useCallback } from "react";
import type { ScopedThreadRef } from "@cmmd-forge/contracts";
import { scopedThreadKey } from "@cmmd-forge/client-runtime";
import { useRouter } from "@tanstack/react-router";
import { buildThreadRouteParams } from "../../threadRoutes";
import { readLocalApi } from "../../localApi";
import { isMacPlatform } from "../../lib/utils";
import { toastManager } from "../ui/toast";
import { useUiStateStore } from "../../uiStateStore";
import { useThreadSelectionStore } from "../../threadSelectionStore";
import { useThreadClipboardCopiers } from "~/hooks/useThreadClipboardCopiers";
import { commitThreadRename } from "~/hooks/commitThreadRename";
import { useThreadShelfActions } from "~/hooks/useThreadShelfActions";
import type { SidebarThreadSummary } from "../../types";
import type { useThreadActions } from "../../hooks/useThreadActions";
import { selectThreadByRef, useStore } from "../../store";
import {
  resolveRetryPromptText,
  resolveSidebarRuntimeActions,
  resolveThreadRowContextMenuOptions,
} from "./useSidebarThreadHandlers.helpers";
import { useCloseSidebarOnNavigate } from "./useCloseSidebarOnNavigate";
import { useDestructiveConfirm } from "./useDestructiveConfirm";
import {
  buildMultiSelectThreadActionMenuItems,
  buildThreadActionMenuItems,
} from "./threadActionMenu.logic";
import {
  deleteSelectedThreads,
  regenerateSelectedTitles,
  runThreadMenuAction,
} from "./sidebarThreadActions";

// ---------------------------------------------------------------------------
// useSidebarThreadHandlers — thread-row interaction callbacks
// ---------------------------------------------------------------------------

export interface UseSidebarThreadHandlersParams {
  projectCwd: string;
  confirmThreadDelete: boolean;
  deleteThread: ReturnType<typeof useThreadActions>["deleteThread"];
  archiveThread: ReturnType<typeof useThreadActions>["archiveThread"];
  /**
   * Reverses an archive from the row menu.
   *
   * Until this existed the settings panel was the only surface in the app that
   * could unarchive, so a thread archived from the sidebar could not be
   * recovered from the sidebar.
   *
   * Optional for the same reason `startNewThreadOnBranch` is: the project rows
   * that also mount this hook never list an archived thread, so plumbing the
   * mutation down six components to reach a branch that cannot be taken would
   * be churn. Where it is absent the shelf slot is omitted rather than
   * offering an archive that cannot apply.
   */
  unarchiveThread?: ReturnType<typeof useThreadActions>["unarchiveThread"] | undefined;
  settleThread: ReturnType<typeof useThreadActions>["settleThread"];
  /** Reverses a settle from the row menu; same shape as `unarchiveThread`. */
  unsettleThread?: ReturnType<typeof useThreadActions>["unsettleThread"] | undefined;
  sidebarThreadByKeyRef: React.RefObject<ReadonlyMap<string, SidebarThreadSummary>>;
  clearSelection: () => void;
  setSelectionAnchor: (key: string) => void;
  toggleThreadSelection: (key: string) => void;
  rangeSelectTo: (key: string, orderedKeys: readonly string[]) => void;
  removeFromSelection: (keys: string[]) => void;
  setRenamingThreadKey: React.Dispatch<React.SetStateAction<string | null>>;
  setRenamingTitle: (title: string) => void;
  renamingCommittedRef: React.MutableRefObject<boolean>;
  /**
   * Starts a new thread in this project seeded with a branch.
   *
   * Optional because two callers mount this hook and only one of them owns a
   * new-thread handler; where it is absent the menu simply omits the item
   * rather than offering an action that would do nothing.
   */
  startNewThreadOnBranch?: ((branch: string) => void) | undefined;
}

export function useSidebarThreadHandlers(params: UseSidebarThreadHandlersParams) {
  const {
    projectCwd,
    confirmThreadDelete,
    deleteThread,
    archiveThread,
    unarchiveThread,
    settleThread,
    unsettleThread,
    sidebarThreadByKeyRef,
    clearSelection,
    setSelectionAnchor,
    toggleThreadSelection,
    rangeSelectTo,
    removeFromSelection,
    setRenamingThreadKey,
    setRenamingTitle,
    renamingCommittedRef,
  } = params;

  const router = useRouter();
  const closeSidebarOnNavigate = useCloseSidebarOnNavigate();
  const markThreadUnread = useUiStateStore((state) => state.markThreadUnread);
  const setThreadPinned = useUiStateStore((state) => state.setThreadPinned);

  // In-app destructive confirm. Replaces the native window.confirm path used by
  // api.dialogs.confirm on web — Chrome MCP / Playwright can't dismiss native
  // dialogs, and the design language drifted from the project Remove flow.
  const destructiveConfirm = useDestructiveConfirm();

  const { copyPathToClipboard, copyBranchToClipboard, copyThreadIdToClipboard } =
    useThreadClipboardCopiers();

  const openPrLink = useCallback((event: React.MouseEvent<HTMLElement>, prUrl: string) => {
    event.preventDefault();
    event.stopPropagation();
    const api = readLocalApi();
    if (!api) {
      toastManager.add({ type: "error", title: "Link opening is unavailable." });
      return;
    }
    void api.shell.openExternal(prUrl).catch((error: unknown) => {
      toastManager.add({
        type: "error",
        title: "Unable to open PR link",
        description: error instanceof Error ? error.message : "An error occurred.",
      });
    });
  }, []);

  const navigateToThread = useCallback(
    (threadRef: ScopedThreadRef) => {
      if (useThreadSelectionStore.getState().selectedThreadKeys.size > 0) clearSelection();
      setSelectionAnchor(scopedThreadKey(threadRef));
      void router.navigate({
        to: "/$environmentId/$threadId",
        params: buildThreadRouteParams(threadRef),
      });
      closeSidebarOnNavigate();
    },
    [clearSelection, closeSidebarOnNavigate, router, setSelectionAnchor],
  );

  const handleThreadClick = useCallback(
    (
      event: React.MouseEvent,
      threadRef: ScopedThreadRef,
      orderedProjectThreadKeys: readonly string[],
    ) => {
      const isMac = isMacPlatform(navigator.platform);
      const isModClick = isMac ? event.metaKey : event.ctrlKey;
      const isShiftClick = event.shiftKey;
      const threadKey = scopedThreadKey(threadRef);
      const currentSelectionCount = useThreadSelectionStore.getState().selectedThreadKeys.size;

      if (isModClick) {
        event.preventDefault();
        toggleThreadSelection(threadKey);
        return;
      }
      if (isShiftClick) {
        event.preventDefault();
        rangeSelectTo(threadKey, orderedProjectThreadKeys);
        return;
      }
      if (currentSelectionCount > 0) clearSelection();
      setSelectionAnchor(threadKey);
      void router.navigate({
        to: "/$environmentId/$threadId",
        params: buildThreadRouteParams(threadRef),
      });
      closeSidebarOnNavigate();
    },
    [
      clearSelection,
      closeSidebarOnNavigate,
      rangeSelectTo,
      router,
      setSelectionAnchor,
      toggleThreadSelection,
    ],
  );

  const handleMultiSelectContextMenu = useCallback(
    async (position: { x: number; y: number }) => {
      const api = readLocalApi();
      if (!api) return;
      const threadKeys = [...useThreadSelectionStore.getState().selectedThreadKeys];
      if (threadKeys.length === 0) return;
      const count = threadKeys.length;

      // Resolved before the menu opens so the label counts what a click would
      // actually do: selecting ten threads of which two can be retitled offers
      // "Regenerate titles (2)", not (10).
      //
      // `sidebarThreadByKeyRef` belongs to ONE `SidebarProjectItem`, so a
      // cross-project selection resolves only the rows from the project whose
      // menu was opened. Those are the keys the label counts and the keys the
      // action uses — the two agree, and nothing is silently skipped.
      const regenerableKeys = threadKeys.filter(
        (key) => sidebarThreadByKeyRef.current?.get(key) != null,
      );
      // Captured here, not re-derived on click. If a running regeneration
      // finishes while the menu is open, a fresh scan would see a NEW idle
      // thread and dispatch more work than the label promised. The click may
      // only ever shrink this set, never grow it.
      const regenerationIdleKeys = regenerableKeys.filter(
        (key) => sidebarThreadByKeyRef.current?.get(key)?.titleRegeneration == null,
      );

      const clicked = await api.contextMenu.show(
        buildMultiSelectThreadActionMenuItems({
          count,
          regenerableCount: regenerableKeys.length,
          regenerationIdleCount: regenerationIdleKeys.length,
        }),
        position,
      );

      if (clicked === "regenerate-title") {
        await regenerateSelectedTitles({
          regenerationIdleKeys,
          sidebarThreadByKeyRef,
          clearSelection,
          removeFromSelection,
        });
        return;
      }

      if (clicked === "mark-unread") {
        for (const threadKey of threadKeys) {
          const thread = sidebarThreadByKeyRef.current?.get(threadKey);
          markThreadUnread(threadKey, thread?.latestTurn?.completedAt);
        }
        clearSelection();
        return;
      }
      if (clicked !== "delete") return;

      await deleteSelectedThreads({
        threadKeys,
        count,
        confirmThreadDelete,
        deleteThread,
        destructiveConfirm,
        sidebarThreadByKeyRef,
        removeFromSelection,
      });
    },
    [
      confirmThreadDelete,
      clearSelection,
      deleteThread,
      destructiveConfirm,
      markThreadUnread,
      removeFromSelection,
      sidebarThreadByKeyRef,
    ],
  );

  const {
    archiveThreadWithCleanup,
    attemptArchiveThread,
    attemptUnarchiveThread,
    attemptSettleThread,
    attemptUnsettleThread,
  } = useThreadShelfActions({
    archiveThread,
    unarchiveThread,
    settleThread,
    unsettleThread,
    confirmDestructiveAction: destructiveConfirm.confirm,
  });

  const cancelRename = useCallback(() => {
    setRenamingThreadKey(null);
  }, [setRenamingThreadKey]);

  const commitRename = useCallback(
    async (threadRef: ScopedThreadRef, newTitle: string, originalTitle: string) => {
      const threadKey = scopedThreadKey(threadRef);
      await commitThreadRename({
        threadRef,
        newTitle,
        originalTitle,
        // Only close the editor if it is still THIS thread's. A rename that
        // resolves after the user has moved on must not shut a box they
        // reopened somewhere else.
        finishRename: () =>
          setRenamingThreadKey((current: string | null) =>
            current === threadKey ? null : current,
          ),
      });
    },
    [setRenamingThreadKey],
  );

  // fallow-ignore-next-line complexity
  const handleThreadContextMenu = useCallback(
    async (threadRef: ScopedThreadRef, position: { x: number; y: number }) => {
      const api = readLocalApi();
      if (!api) return;
      const threadKey = scopedThreadKey(threadRef);
      const thread = sidebarThreadByKeyRef.current?.get(threadKey) ?? null;
      if (!thread) return;
      const threadWorkspacePath = thread.worktreePath ?? projectCwd ?? null;
      const threadDetail = selectThreadByRef(useStore.getState(), threadRef);
      const retryPromptText = resolveRetryPromptText(threadDetail);
      const runtimeActions = resolveSidebarRuntimeActions({
        sessionStatus: thread.session?.status,
        activeTurnId: thread.session?.activeTurnId,
        hasRetryPrompt: retryPromptText !== null,
      });
      const clicked = await api.contextMenu.show(
        buildThreadActionMenuItems(
          resolveThreadRowContextMenuOptions({
            thread,
            isPinned: Boolean(useUiStateStore.getState().pinnedThreadById[threadKey]),
            runtimeActions,
            // The branch name is always enough to COPY. Starting a thread on it
            // needs a handler this caller may not own, so that item — and only
            // that item — is gated on having one.
            canStartNewThreadOnBranch: params.startNewThreadOnBranch != null,
            canUnarchive: unarchiveThread != null,
            canUnsettle: unsettleThread != null,
          }),
        ),
        position,
      );
      if (clicked === null) return;

      await runThreadMenuAction({
        clicked,
        threadRef,
        thread,
        threadKey,
        threadWorkspacePath,
        retryPromptText,
        threadDetail,
        confirmThreadDelete,
        copyPathToClipboard,
        copyThreadIdToClipboard,
        copyBranchToClipboard,
        startNewThreadOnBranch: params.startNewThreadOnBranch ?? (() => {}),
        deleteThread,
        destructiveConfirm,
        markThreadUnread,
        setRenamingThreadKey,
        setRenamingTitle,
        setThreadPinned,
        renamingCommittedRef,
        attemptArchiveThread,
        attemptUnarchiveThread,
        attemptSettleThread,
        attemptUnsettleThread,
      });
    },
    [
      attemptArchiveThread,
      attemptUnarchiveThread,
      attemptSettleThread,
      attemptUnsettleThread,
      confirmThreadDelete,
      copyPathToClipboard,
      copyThreadIdToClipboard,
      copyBranchToClipboard,
      params.startNewThreadOnBranch,
      deleteThread,
      destructiveConfirm,
      markThreadUnread,
      projectCwd,
      renamingCommittedRef,
      sidebarThreadByKeyRef,
      setThreadPinned,
      setRenamingThreadKey,
      setRenamingTitle,
      // Read in the builder call above as `canUnarchive`. Left out, a caller
      // that gains the handler after first render keeps a menu that says it
      // cannot unarchive.
      unarchiveThread,
      // Same reason, for `canUnsettle`.
      unsettleThread,
    ],
  );

  return {
    openPrLink,
    navigateToThread,
    handleThreadClick,
    handleMultiSelectContextMenu,
    attemptArchiveThread,
    archiveThreadWithCleanup,
    attemptSettleThread,
    attemptUnsettleThread,
    cancelRename,
    commitRename,
    handleThreadContextMenu,
    /** Mount once so archive and delete confirmations can render. */
    DestructiveConfirmDialog: destructiveConfirm.DestructiveConfirmDialog,
  };
}
